import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';

import { IOption } from '../components/SelectSync';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

interface IItens {
  GeiCodigo?: number;
  GeiOrdem: number;
  GeiGexCodigo: number;
  GeiProCodigo: number;
  procedimento?: IProcedimento;
}

interface IPerfil {
  PerCodigo: string;
  PerNome: string;
}
interface IProcedimento {
  ProCodigo: number;
  ProNome: string;
  perfil?: IPerfil;
}

export interface IGrupo {
  GexCodigo?: number;
  GexNome: string;
  GexSelecionado: boolean;
  GexAtivo: boolean;
  GexPerCodigo: number;
  perfil: IPerfil;
  itens: IItens[];
}

interface IOptionsGrupo {
  optionGrupos: IOption[];
}
interface IGrupos {
  grupos: IGrupo[];
}

interface GrupoExamesContextData {
  loading: boolean;
  optionsGrupo: IOptionsGrupo;
  grupos: IGrupos;
  buscaGrupo(): Promise<void>;
  limparGrupo(): void;
}

const GrupoExamesContext = createContext<GrupoExamesContextData>(
  {} as GrupoExamesContextData,
);

const GrupoExameProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();
  const [grupos, setGrupos] = useState<IGrupos>(() => {
    const grs = localStorage.getItem('@AgendaFacil:grupos');
    if (grs) return JSON.parse(grs);

    return {} as IGrupos;
  });

  const [optionsGrupo, setOptionsGrupo] = useState<IOptionsGrupo>(() => {
    const optionGrupos = localStorage.getItem('@AgendaFacil:optionsGrupo');
    if (optionGrupos) return JSON.parse(optionGrupos);

    return {} as IOptionsGrupo;
  });

  const buscaGrupo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<[IGrupo[], number, number[]]>(
        `/grupoexames?limite=1000`,
      );

      const options: IOption[] = response.data[0].map((op: IGrupo) => ({
        value: op.GexCodigo?.toString() || '',
        label: op.GexNome,
        isDisabled: !op.GexAtivo,
      }));

      const gruposLocal = response.data[0]
        .map(gr => {
          return {
            ...gr,
          };
        })
        .sort((a, b) => b.GexPerCodigo - a.GexPerCodigo);
      const localGrupos: IGrupos = {
        grupos: gruposLocal,
      };

      const localOptions: IOptionsGrupo = {
        optionGrupos: options,
      };

      localStorage.setItem('@AgendaFacil:grupos', JSON.stringify(localGrupos));
      localStorage.setItem(
        '@AgendaFacil:optionsGrupo',
        JSON.stringify(localOptions),
      );
      setGrupos(localGrupos);

      setOptionsGrupo(localOptions);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar grupos',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    async function load(): Promise<void> {
      const tokenLocal = localStorage.getItem('@AgendaFacil:token');
      if (tokenLocal) {
        buscaGrupo();
      }
    }
    load();
  }, [buscaGrupo]);

  const limparGrupo = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:optionsGrupo');
    localStorage.removeItem('@AgendaFacil:grupos');
    setGrupos({} as IGrupos);
    setOptionsGrupo({} as IOptionsGrupo);
  }, []);

  return (
    <GrupoExamesContext.Provider
      value={{
        loading,
        grupos,
        optionsGrupo,
        buscaGrupo,
        limparGrupo,
      }}
    >
      {children}
    </GrupoExamesContext.Provider>
  );
};

function useGrupoExame(): GrupoExamesContextData {
  const context = useContext(GrupoExamesContext);

  if (!context) {
    throw new Error('useGrupoExame deve ser usado com o GrupoExameProvider');
  }

  return context;
}

export { GrupoExameProvider, useGrupoExame };
